/**
 * Date utility functions for consistent date handling across the application
 */

import { format, formatDistance, isValid } from 'date-fns';
import { zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';

// Set the application timezone to Mountain Time
export const APP_TIMEZONE = 'America/Denver';

/**
 * Formats a date string or Date object to a human-readable format
 * @param date Date string or Date object
 * @param formatString Format string (default: 'MMM d, yyyy')
 * @returns Formatted date string
 */
export function formatDate(date: string | Date | null | undefined, formatString = 'MMM d, yyyy'): string {
  if (!date) return 'No date';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (!isValid(dateObj)) return 'Invalid date';
    
    // Convert UTC date to Mountain Time
    const zonedDate = utcToZonedTime(dateObj, APP_TIMEZONE);
    return format(zonedDate, formatString);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
}

/**
 * Formats a date relative to the current date (e.g., "2 days ago")
 * @param date Date string or Date object
 * @returns Relative date string
 */
export function formatRelativeDate(date: string | Date | null | undefined): string {
  if (!date) return 'No date';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (!isValid(dateObj)) return 'Invalid date';
    
    // Convert UTC date to Mountain Time
    const zonedDate = utcToZonedTime(dateObj, APP_TIMEZONE);
    const now = utcToZonedTime(new Date(), APP_TIMEZONE);
    
    return formatDistance(zonedDate, now, { addSuffix: true });
  } catch (error) {
    console.error('Error formatting relative date:', error);
    return 'Invalid date';
  }
}

/**
 * Converts a local date to UTC with timezone consideration
 * @param date Local date (browser timezone)
 * @returns UTC date
 */
export function toUTC(date: Date): Date {
  return zonedTimeToUtc(date, APP_TIMEZONE);
}

/**
 * Converts a UTC date to the application timezone (Mountain Time)
 * @param date UTC date
 * @returns Date in Mountain Time
 */
export function fromUTC(date: Date): Date {
  return utcToZonedTime(date, APP_TIMEZONE);
}

/**
 * Gets the current date in the application timezone
 * @returns Current date in Mountain Time
 */
export function getCurrentDate(): Date {
  return utcToZonedTime(new Date(), APP_TIMEZONE);
}

/**
 * Formats a date for input[type="date"] fields (YYYY-MM-DD)
 * @param date Date to format
 * @returns Date string in YYYY-MM-DD format
 */
export function formatForDateInput(date: Date | string | null | undefined): string {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (!isValid(dateObj)) return '';
    
    // For date inputs, we need the local date without timezone conversion
    return format(dateObj, 'yyyy-MM-dd');
  } catch (error) {
    console.error('Error formatting date for input:', error);
    return '';
  }
}

/**
 * Formats a duration in seconds to a human-readable string (e.g. "2h 30m 15s")
 * @param seconds Total duration in seconds
 * @returns Formatted duration string
 */
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  
  const parts = [];
  
  if (hours > 0) {
    parts.push(`${hours}h`);
  }
  
  if (minutes > 0 || hours > 0) {
    parts.push(`${minutes}m`);
  }
  
  parts.push(`${remainingSeconds}s`);
  
  return parts.join(' ');
}